import { Building2, TrendingUp, Handshake, BarChart3, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";

const PartnersSection = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();

  const partnerBenefits = [
    {
      icon: Building2,
      titleKey: "partners.benefit.whitelabel.title",
      descKey: "partners.benefit.whitelabel.desc",
    },
    {
      icon: TrendingUp,
      titleKey: "partners.benefit.revenue.title",
      descKey: "partners.benefit.revenue.desc",
    },
    {
      icon: Handshake,
      titleKey: "partners.benefit.sales.title",
      descKey: "partners.benefit.sales.desc",
    },
    {
      icon: BarChart3,
      titleKey: "partners.benefit.dashboard.title",
      descKey: "partners.benefit.dashboard.desc",
    },
  ];

  const partnerTypes = [
    {
      typeKey: "partners.type.agencies",
      descKey: "partners.type.agencies.desc",
    },
    {
      typeKey: "partners.type.consultants",
      descKey: "partners.type.consultants.desc",
    },
    {
      typeKey: "partners.type.it",
      descKey: "partners.type.it.desc",
    },
  ];

  return (
    <section id="partners" className="synra-section relative bg-card/50">
      {/* Decorative Elements */}
      <div 
        className="absolute top-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
        style={{ insetInlineEnd: 0 }}
      />
      
      <div className="synra-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Content */}
          <div className="text-start">
            <span className="text-primary font-medium text-sm uppercase tracking-wider mb-4 block">
              {t("partners.label")}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {t("partners.title")}{' '}
              <span className="synra-gradient-text">{t("partners.titleHighlight")}</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
              {t("partners.subtitle")}
            </p>

            {/* Partner Types */}
            <div className="mb-10">
              <h3 className="font-heading font-semibold text-foreground mb-4 text-base">{t("partners.ideal.title")}</h3>
              <div className="space-y-3">
                {partnerTypes.map((partner) => (
                  <div key={partner.typeKey} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-foreground">{t(partner.typeKey)}:</span>
                      <span className="text-muted-foreground ms-1">{t(partner.descKey)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Button variant="synra" size="lg" className="group" onClick={() => navigate("/auth?tab=partner")}>
              <span>{t("partners.cta")}</span>
              <ArrowRight className={`w-5 h-5 transition-transform duration-200 group-hover:translate-x-1 ${isRTL ? 'rtl-flip' : ''}`} />
            </Button>
          </div>

          {/* Benefits Grid */}
          <div className="grid sm:grid-cols-2 gap-6">
            {partnerBenefits.map((benefit) => (
              <div
                key={benefit.titleKey}
                className="synra-card group text-start"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-foreground text-lg mb-2">
                  {t(benefit.titleKey)}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t(benefit.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;